import { useState, useEffect } from 'react';
import api from '../../../hooks/useApi';
import Modal from '../../shared/Modal';
import { fmtD, fmtDY } from './planConstants';

/*
  Bringing the plan up to date, the way Microsoft Project's Update Project does.

  A plan showing work that has not started sitting in the past is not a plan,
  it is a record of an intention, and it is the single fastest way for a chart
  to lose everybody's trust.

  Two different problems get two different answers:

    • not started, and in the past  → move it forward, which is mechanical
    • started, and should already have finished → left alone, because the only
      honest fix is a new estimate from whoever is doing it, and we do not split
      a task in two the way Project would

  Nothing moves until it is previewed and confirmed, and each task can be
  excluded.
*/

export default function StatusDatePanel({ sowId, statusDate, onClose, onApplied }) {
  const [date, setDate] = useState(statusDate || new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    api.get(`sows/${sowId}/reschedule-incomplete`, { status_date: date })
      .then(p => { if (!cancelled) setPreview(p); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not work out what would move.'); });
    return () => { cancelled = true; };
  }, [sowId, date]);

  const toggle = (id) => setExcluded(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const moving = (preview?.move || []).filter(m => !excluded.has(m.task_id));

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api.post(`sows/${sowId}/reschedule-incomplete`, {
        status_date: date,
        task_ids: moving.map(m => m.task_id),
      });
      onApplied(r);
    } catch (e) {
      setError(e.message || 'That did not work.');
      setBusy(false);
    }
  };

  return (
    <Modal title="Bring the plan up to date" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={apply} disabled={busy || !moving.length}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
          {busy ? 'Moving…' : moving.length ? `Move ${moving.length} task${moving.length !== 1 ? 's' : ''} forward` : 'Nothing to move'}
        </button>
      </>
    }>
      <div className="space-y-3">
        {error && <div className="text-[9px] text-red-600 bg-red-50 px-2 py-1.5 rounded">{error}</div>}

        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status date</label>
            <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="text-[8px] text-gray-500 flex-1 pb-1">
            The date the plan is reported as at. Work that had not started before it is moved to it;
            work already under way is left alone.
            {preview?.status_date && preview.status_date !== date && (
              <span className="block text-gray-400">
                {fmtDY(date)} is not a working day, so {fmtDY(preview.status_date)} is used.
              </span>
            )}
          </div>
        </div>

        {!preview ? (
          <div className="py-6 text-center text-[10px] text-gray-400">Checking…</div>
        ) : (
          <>
            {/* Not started, in the past */}
            <div className="border border-gray-200 rounded">
              <div className="px-2 py-1 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <span className="text-[8px] font-bold uppercase tracking-wider text-gray-500">
                  Not started, and already in the past
                </span>
                <span className="text-[8px] text-gray-400">{preview.move.length}</span>
                {preview.move.length > 0 && (
                  <button onClick={() => setExcluded(excluded.size ? new Set() : new Set(preview.move.map(m => m.task_id)))}
                          className="ml-auto text-[8px] text-gray-400 hover:text-navy">
                    {excluded.size ? 'select all' : 'select none'}
                  </button>
                )}
              </div>
              {preview.move.length === 0 ? (
                <div className="px-3 py-3 text-[9px] text-gray-400 italic">
                  Nothing. Every unstarted task is where it should be.
                </div>
              ) : (
                <div className="overflow-x-auto"><table className="w-full">
                  <tbody>
                    {preview.move.map(m => (
                      <tr key={m.task_id} className="border-b border-gray-50 last:border-0">
                        <td className="px-2 py-1 w-6">
                          <input type="checkbox" className="w-3 h-3 accent-navy"
                                 checked={!excluded.has(m.task_id)} onChange={() => toggle(m.task_id)} />
                        </td>
                        <td className="px-1 py-1 text-[7.5px] font-mono text-gray-300 w-10">{m.wbs_code}</td>
                        <td className="px-1 py-1 text-[9px] text-gray-700">{m.name}</td>
                        <td className="px-2 py-1 text-[8px] text-gray-400 whitespace-nowrap w-40 text-right">
                          {fmtD(m.from)} → <span className="font-semibold text-gray-700">{fmtD(m.to)}</span>
                          <span className="text-red-500 ml-1.5">{m.days_late}d late</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>

            {/* Started, and overdue */}
            {preview.needs_estimate.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded">
                <div className="px-2 py-1 border-b border-amber-100">
                  <span className="text-[8px] font-bold uppercase tracking-wider text-amber-800">
                    Under way, and should have finished
                  </span>
                  <span className="text-[8px] text-amber-600 ml-2">{preview.needs_estimate.length}</span>
                </div>
                <div className="px-2 py-1.5 text-[8px] text-amber-800">
                  These are not moved. Somebody is part way through them, so the finish date needs a new
                  estimate from whoever is doing the work rather than an automatic shove.
                </div>
                <div className="overflow-x-auto"><table className="w-full">
                  <tbody>
                    {preview.needs_estimate.map(m => (
                      <tr key={m.task_id} className="border-t border-amber-100">
                        <td className="px-2 py-1 text-[7.5px] font-mono text-amber-400 w-10">{m.wbs_code}</td>
                        <td className="px-1 py-1 text-[9px] text-amber-900">{m.name}</td>
                        <td className="px-2 py-1 text-[8px] text-amber-700 text-right whitespace-nowrap">
                          {m.completion_pct}% done · finish was {fmtD(m.end_date)} ·{' '}
                          <span className="font-semibold">{m.days_overdue}d overdue</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}

            <div className="text-[7.5px] text-gray-400">
              Moving a task sets a "start no earlier than" constraint on it, which shows on the row and can be
              cleared from the task editor. Anything depending on it reschedules behind it.
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
